import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { icon } from './icons';

/**
 * <cadence-file-upload> — reusable drag-and-drop + click upload zone. Presentational:
 * it collects files and emits a `files` CustomEvent (detail: File[]); the host handles
 * validation/metadata/upload. Set `accept`, `multiple`, `hint`, and `busy`.
 */
@customElement('cadence-file-upload')
export class CadenceFileUpload extends LitElement {
  @property() accept = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm';
  @property({ type: Boolean }) multiple = false;
  @property() label = 'Drop files here or click to upload';
  @property() hint = 'JPEG, PNG, WebP, GIF, MP4, MOV or WebM';
  @property({ type: Boolean }) busy = false;
  @state() private dragOver = false;
  @query('input[type=file]') private fileInput!: HTMLInputElement;

  static styles = css`
    :host { display: block; }
    input[type=file] { display: none; }
    .drop {
      border: 2px dashed var(--color-border); border-radius: var(--radius-md);
      padding: var(--space-5); text-align: center; color: var(--color-text-secondary);
      cursor: pointer; transition: border-color 0.15s, background 0.15s;
    }
    .drop:hover, .drop.over { border-color: var(--color-primary); background: var(--color-bg-secondary); }
    .drop svg { width: 30px; height: 30px; margin-bottom: 6px; }
    .drop .hint { font-size: 0.78rem; margin-top: 2px; }
  `;

  private emit(list: FileList | null | undefined) {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    this.dispatchEvent(new CustomEvent('files', { detail: this.multiple ? files : files.slice(0, 1), bubbles: true, composed: true }));
  }

  private onPick(e: Event) {
    const input = e.target as HTMLInputElement;
    this.emit(input.files);
    input.value = ''; // allow re-picking the same file
  }
  private onDrop(e: DragEvent) {
    e.preventDefault(); this.dragOver = false;
    if (!this.busy) this.emit(e.dataTransfer?.files);
  }

  render() {
    return html`
      <div class="drop ${this.dragOver ? 'over' : ''}"
        @click=${() => !this.busy && this.fileInput?.click()}
        @dragover=${(e: DragEvent) => { e.preventDefault(); this.dragOver = true; }}
        @dragleave=${() => (this.dragOver = false)}
        @drop=${(e: DragEvent) => this.onDrop(e)}>
        ${icon('upload', 30)}
        <div>${this.busy ? 'Uploading…' : this.label}</div>
        <div class="hint">${this.hint}</div>
      </div>
      <input type="file" accept=${this.accept} ?multiple=${this.multiple} @change=${this.onPick} />
    `;
  }
}
